import { tx } from "@/i18n/tx";
import { useEffect, useState } from "react";
import { Download, Share, PlusSquare, MoreVertical, X } from "lucide-react";

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const isIOSOtherBrowser = () => isIOS() && /crios|fxios|edgios|opios|gsa\//i.test(navigator.userAgent);
const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

export const InstallPWA = ({ variant = "card" }) => {
  const [canPrompt, setCanPrompt] = useState(!!window.__deferredInstallPrompt);
  const [installed, setInstalled] = useState(isStandalone());
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    const onInstallable = () => setCanPrompt(true);
    const onInstalled = () => { setInstalled(true); setCanPrompt(false); };
    window.addEventListener("pwa-installable", onInstallable);
    window.addEventListener("pwa-installed", onInstalled);
    return () => {
      window.removeEventListener("pwa-installable", onInstallable);
      window.removeEventListener("pwa-installed", onInstalled);
    };
  }, []);

  const handleClick = async () => {
    const dp = window.__deferredInstallPrompt;
    if (dp) {
      dp.prompt();
      try { await dp.userChoice; } catch (err) { console.debug(tx("Install prompt dismissed:"), err?.message || err); }
      window.__deferredInstallPrompt = null;
      setCanPrompt(false);
    } else {
      setShowHelp(true);
    }
  };

  if (installed && variant === "link") return null;
  if (installed) {
    return (
      <div className="rounded-3xl bg-anima-sage/15 border border-anima-ink/5 px-5 py-4 flex items-center gap-3" data-testid="pwa-installed-note">
        <Download size={18} className="text-anima-sagedeep" />
        <p className="text-sm text-anima-ink">{tx("Ascolta Anima è installata su questo dispositivo.")}</p>
      </div>
    );
  }

  return (
    <>
      {variant === "link" ? (
        <button
          onClick={handleClick}
          data-testid="landing-install-link"
          className="mx-auto mt-1 flex items-center gap-2 text-sm text-anima-muted underline-offset-4 hover:underline py-2"
        >
          <Download size={15} />
          {tx("Installa Ascolta Anima")}
        </button>
      ) : (
      <button
        onClick={handleClick}
        data-testid="install-pwa-btn"
        className="w-full rounded-3xl bg-anima-sagedeep text-anima-ivory px-5 py-4 flex items-center justify-between active:scale-[0.98] transition-transform duration-200"
      >
        <span className="flex items-center gap-3">
          <Download size={18} />
          <span className="text-base">{tx("Installa Ascolta Anima")}</span>
        </span>
        <span className="text-[11px] uppercase tracking-widest opacity-80">{canPrompt ? tx("Installa") : tx("Come fare")}</span>
      </button>
      )}

      {showHelp && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-anima-ink/35 backdrop-blur-sm px-4 py-4" onClick={() => setShowHelp(false)}>
          <div className="w-full max-w-md bg-anima-ivory rounded-3xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="install-help">
            <div className="flex items-center justify-between mb-4">
              <p className="font-serif text-2xl text-anima-ink">{tx("Installa l'app")}</p>
              <button onClick={() => setShowHelp(false)} data-testid="install-help-close"><X size={20} className="text-anima-muted" /></button>
            </div>

            {isIOS() ? (
              <div className="space-y-4" data-testid="install-help-ios">
                {isIOSOtherBrowser() && (
                  <p className="text-[15px] text-anima-ink/85 leading-relaxed rounded-2xl bg-anima-pink/30 px-4 py-3" data-testid="install-help-ios-safari">{tx("Per installare l'app, apri ascoltaanima.it in Safari.")}</p>
                )}
                <p className="text-[15px] text-anima-ink/85 leading-relaxed">{tx("Su iPhone e iPad, con")}<b>{tx("Safari")}</b>:</p>
                <div className="flex items-start gap-3">
                  <span className="shrink-0 w-7 h-7 rounded-full bg-anima-pink/60 flex items-center justify-center text-sm">1</span>
                  <p className="text-[15px] text-anima-ink/85 leading-relaxed flex items-center gap-1.5 flex-wrap">{tx("Tocca il pulsante")}<b>{tx("Condividi")}</b> <Share size={16} className="inline text-anima-sagedeep" />{tx("nella barra in basso.")}</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="shrink-0 w-7 h-7 rounded-full bg-anima-pink/60 flex items-center justify-center text-sm">2</span>
                  <p className="text-[15px] text-anima-ink/85 leading-relaxed flex items-center gap-1.5 flex-wrap">{tx("Scorri e scegli")}<b>{tx("Aggiungi a Home")}</b> <PlusSquare size={16} className="inline text-anima-sagedeep" />.</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="shrink-0 w-7 h-7 rounded-full bg-anima-pink/60 flex items-center justify-center text-sm">3</span>
                  <p className="text-[15px] text-anima-ink/85 leading-relaxed">{tx("Conferma con")}<b>{tx("Aggiungi")}</b>{tx(": l'icona apparirà sulla schermata Home.")}</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4" data-testid="install-help-android">
                <p className="text-[15px] text-anima-ink/85 leading-relaxed">{tx("Sul tuo dispositivo, con")}<b>{tx("Chrome")}</b>{tx("o un browser compatibile:")}</p>
                <div className="flex items-start gap-3">
                  <span className="shrink-0 w-7 h-7 rounded-full bg-anima-pink/60 flex items-center justify-center text-sm">1</span>
                  <p className="text-[15px] text-anima-ink/85 leading-relaxed flex items-center gap-1.5 flex-wrap">{tx("Apri il menu")}<MoreVertical size={16} className="inline text-anima-sagedeep" />{tx("in alto a destra.")}</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="shrink-0 w-7 h-7 rounded-full bg-anima-pink/60 flex items-center justify-center text-sm">2</span>
                  <p className="text-[15px] text-anima-ink/85 leading-relaxed">{tx("Scegli")}<b>{tx("Installa app")}</b>{tx("(oppure")}<b>{tx("Aggiungi a schermata Home")}</b>).</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="shrink-0 w-7 h-7 rounded-full bg-anima-pink/60 flex items-center justify-center text-sm">3</span>
                  <p className="text-[15px] text-anima-ink/85 leading-relaxed">{tx("Conferma: Ascolta Anima si aprirà come un'app a tutto schermo.")}</p>
                </div>
              </div>
            )}

            <button onClick={() => setShowHelp(false)} className="mt-6 w-full rounded-full border border-anima-ink/15 text-anima-ink py-3" data-testid="install-help-ok">{tx("Ho capito")}</button>
          </div>
        </div>
      )}
    </>
  );
};
